import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ShieldCheck, Search, FilterX, ChevronLeft, ChevronRight, History, Eye,
} from "lucide-react";

const ROLE_BADGE = {
  admin: "bg-[#F4EAE6] text-[#A64A29] border-[#E8C9BB]",
  encargado: "bg-[#FDF3E1] text-[#A6751F] border-[#F0DEB2]",
  repartidor: "bg-[#DAE6D7] text-[#3F6B3C] border-[#BCD3B7]",
  elaborador: "bg-[#E6DDF4] text-[#5C3F8C] border-[#C9BBE8]",
  cliente: "bg-[#E0EAF5] text-[#365E91] border-[#B8C9DD]",
};

const ACTION_LABEL = {
  "pedido.create": "Pedido creado",
  "pedido.update": "Pedido editado",
  "pedido.delete": "Pedido eliminado",
  "pedido.confirm": "Pedido confirmado",
  "pedido.estado": "Estado cambiado",
  "pedido.duplicate": "Pedido duplicado",
  "pago.create": "Pago registrado",
  "pago.delete": "Pago eliminado",
  "cuenta_corriente.toggle": "Cta. corriente",
  "producto.create": "Producto creado",
  "producto.update": "Producto editado",
  "producto.delete": "Producto eliminado",
  "producto.price": "Precio cambiado",
  "user.create": "Usuario creado",
  "user.update": "Usuario editado",
  "user.delete": "Usuario eliminado",
  "user.password": "Contraseña cambiada",
  "insumo.create": "Insumo cargado",
  "insumo.update": "Insumo editado",
  "insumo.delete": "Insumo eliminado",
};

const PAGE_SIZE = 50;

export default function Auditoria() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [facets, setFacets] = useState({ actions: [], roles: [], entity_types: [], users: [] });
  const [filters, setFilters] = useState({
    user_id: "", role: "", action: "", entity_type: "",
    desde: "", hasta: "", q: "",
  });
  const [selected, setSelected] = useState(null);

  const params = useMemo(() => {
    const p = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    for (const [k, v] of Object.entries(filters)) {
      if (v) p[k] = v;
    }
    return p;
  }, [filters, page]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/audit", { params })
      .then((r) => { setRows(r.data.rows); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/audit/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const reset = () => {
    setFilters({ user_id: "", role: "", action: "", entity_type: "", desde: "", hasta: "", q: "" });
    setPage(0);
  };
  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(0); };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        eyebrow="Seguridad"
        title="Auditoría de movimientos"
        subtitle={`Histórico de acciones. Se conserva por 90 días. ${total} registro${total === 1 ? "" : "s"} en este filtro.`}
      >
        <Button variant="outline" className="border-[#E5E0D8] gap-2" onClick={reset} data-testid="aud-reset">
          <FilterX className="h-4 w-4" /> Limpiar
        </Button>
      </PageHeader>

      {/* Filters */}
      <Card className="p-4 sm:p-5 border-[#E5E0D8] rounded-2xl bg-white mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-1.5 block">Búsqueda libre</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C8C8C]" />
              <Input
                value={filters.q}
                onChange={(e) => setF("q", e.target.value)}
                placeholder="Buscar por descripción, usuario, email..."
                className="pl-9 h-10 bg-white"
                data-testid="aud-q"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-1.5 block">Rol</Label>
            <Select value={filters.role || "__all__"} onValueChange={(v) => setF("role", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-10 bg-white" data-testid="aud-role"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los roles</SelectItem>
                {facets.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-1.5 block">Acción</Label>
            <Select value={filters.action || "__all__"} onValueChange={(v) => setF("action", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-10 bg-white" data-testid="aud-action"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {facets.actions.map((a) => <SelectItem key={a} value={a}>{ACTION_LABEL[a] || a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-1.5 block">Entidad</Label>
            <Select value={filters.entity_type || "__all__"} onValueChange={(v) => setF("entity_type", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-10 bg-white" data-testid="aud-entity"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {facets.entity_types.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-1.5 block">Usuario</Label>
            <Select value={filters.user_id || "__all__"} onValueChange={(v) => setF("user_id", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-10 bg-white" data-testid="aud-user"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {facets.users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.user_name} ({u.role}) · {u.count}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-1.5 block">Desde</Label>
            <DatePicker value={filters.desde} onChange={(v) => setF("desde", v || "")} testid="aud-desde" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-1.5 block">Hasta</Label>
            <DatePicker value={filters.hasta} onChange={(v) => setF("hasta", v || "")} testid="aud-hasta" />
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card className="border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F9F6F0] border-b border-[#E5E0D8]">
              <tr className="text-[11px] uppercase tracking-[0.14em] text-[#8C8C8C]">
                <th className="px-5 py-3">Fecha</th>
                <th className="px-5 py-3">Usuario</th>
                <th className="px-5 py-3">Rol</th>
                <th className="px-5 py-3">Acción</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center text-[#8C8C8C] py-12">Cargando...</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12">
                  <ShieldCheck className="h-10 w-10 mx-auto text-[#C85A32] opacity-50 mb-2" />
                  <div className="font-display font-semibold text-base">Sin registros</div>
                  <p className="text-xs text-[#8C8C8C] mt-1">Probá cambiar los filtros.</p>
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F0EBE0] hover:bg-[#FBF8F2]" data-testid={`aud-row-${r.id}`}>
                  <td className="px-5 py-3 text-[12px] font-mono text-[#595959] whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-[#1A1A1A] text-sm">{r.user_name}</div>
                    <div className="text-[11px] text-[#8C8C8C]">{r.user_email}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border ${ROLE_BADGE[r.role] || ROLE_BADGE.admin}`}>
                      {r.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm font-medium text-[#1A1A1A]">
                    {ACTION_LABEL[r.action] || r.action}
                  </td>
                  <td className="px-5 py-3 text-sm text-[#595959] max-w-[420px] truncate">{r.description}</td>
                  <td className="px-5 py-3 text-right">
                    <button type="button" onClick={() => setSelected(r)} className="text-[#595959] hover:text-[#C85A32] inline-flex items-center gap-1 text-[12px]" data-testid={`aud-view-${r.id}`}>
                      <Eye className="h-3.5 w-3.5" /> Detalle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[#F0EBE0]">
          {loading && <div className="text-center text-[#8C8C8C] py-10">Cargando...</div>}
          {!loading && rows.length === 0 && (
            <div className="text-center py-10 text-[#8C8C8C]">Sin registros.</div>
          )}
          {!loading && rows.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelected(r)}
              className="block w-full text-left p-4 hover:bg-[#FBF8F2]"
              data-testid={`aud-card-${r.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-[0.1em] border ${ROLE_BADGE[r.role] || ROLE_BADGE.admin}`}>
                  {r.role}
                </span>
                <span className="text-[11px] text-[#8C8C8C] font-mono">
                  {new Date(r.created_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}
                </span>
              </div>
              <div className="font-display font-semibold text-sm">{ACTION_LABEL[r.action] || r.action}</div>
              <div className="text-[11px] text-[#8C8C8C] mb-1">{r.user_name}</div>
              <div className="text-[12px] text-[#595959]">{r.description}</div>
            </button>
          ))}
        </div>

        {/* Pagination */}
        {!loading && total > PAGE_SIZE && (
          <div className="border-t border-[#E5E0D8] px-4 py-3 flex items-center justify-between text-sm">
            <div className="text-[#595959]">
              Página {page + 1} de {pages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm" className="border-[#E5E0D8]"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="aud-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline" size="sm" className="border-[#E5E0D8]"
                disabled={page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="aud-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <History className="h-5 w-5 text-[#C85A32]" />
              {selected ? (ACTION_LABEL[selected.action] || selected.action) : "Detalle"}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Usuario</div>
                  <div className="font-semibold">{selected.user_name}</div>
                  <div className="text-[11px] text-[#8C8C8C]">{selected.user_email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Rol</div>
                  <div><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border ${ROLE_BADGE[selected.role] || ROLE_BADGE.admin}`}>{selected.role}</span></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Fecha</div>
                  <div className="font-mono text-[12px]">{new Date(selected.created_at).toLocaleString("es-AR")}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Entidad</div>
                  <div className="font-mono text-[12px]">{selected.entity_type} {selected.entity_id ? `· ${selected.entity_id.slice(0, 8)}…` : ""}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Descripción</div>
                <div>{selected.description}</div>
              </div>
              {(selected.before || selected.after) && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#A64A29]">Antes</div>
                    <pre className="font-mono text-[11px] bg-[#F9F6F0] border border-[#E5E0D8] rounded-lg p-2 overflow-auto max-h-64">{JSON.stringify(selected.before ?? null, null, 2)}</pre>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#3F6B3C]">Después</div>
                    <pre className="font-mono text-[11px] bg-[#F9F6F0] border border-[#E5E0D8] rounded-lg p-2 overflow-auto max-h-64">{JSON.stringify(selected.after ?? null, null, 2)}</pre>
                  </div>
                </div>
              )}
              {(selected.ip || selected.user_agent) && (
                <div className="text-[10px] text-[#8C8C8C] pt-2 border-t border-[#E5E0D8]">
                  {selected.ip && <>IP: <span className="font-mono">{selected.ip}</span> · </>}
                  {selected.user_agent && <>UA: <span className="font-mono">{(selected.user_agent || "").slice(0, 80)}</span></>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
